/**
 * AdminProcurementListPage — Issue #366.
 *
 * Lists administrative procurement ledger rows with status / category
 * filters, supports inline create / confirm / void / archive / manual
 * payment-application trigger via {@link useAdminProcurementMutations}.
 *
 * 红线:
 *   - 金额字段 (amountDecimal) 仅在持有 admin_procurement:finance 权限时
 *     展示原值；其他角色看占位符。
 *   - 列表 / 详情 / 操作都走 generated api-client；不构造假数据。
 *   - “生成付款申请” 走后端 command；finance #374 未上线返回
 *     paymentStatus=not_available，UI 展示“财务服务待上线”受控空态。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_PROCUREMENT_PERMISSIONS,
  canApproveAdminProcurement,
  canManageAdminProcurement,
  canSeeAdminProcurementFinance,
  hasPermission,
} from '@/lib/adminProcurementPermissions';
import {
  ADMIN_PROCUREMENT_CATEGORY_LABELS,
  ADMIN_PROCUREMENT_PAYMENT_STATUS_LABELS,
  ADMIN_PROCUREMENT_STATUS_LABELS,
  useAdminProcurementMutations,
  useAdminProcurements,
  type AdminProcurementDto,
  type CreateAdminProcurementDto,
  type RequestPaymentApplicationDto,
} from '@/hooks/useAdminProcurement';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('zh-CN');
}

function fmtMoney(value: string | null | undefined, canSee: boolean): string {
  if (!canSee) return '受限';
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

const STATUS_BADGE_CLASS: Readonly<Record<AdminProcurementDto['status'], string>> = {
  draft: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-blue-100 text-blue-700',
  payment_requested: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-700',
  voided: 'bg-red-100 text-red-700',
  archived: 'bg-zinc-100 text-zinc-500',
};

const PAYMENT_BADGE_CLASS: Readonly<
  Record<AdminProcurementDto['paymentStatus'], string>
> = {
  not_requested: 'bg-slate-100 text-slate-600',
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-700',
  voided: 'bg-red-100 text-red-700',
  not_available: 'bg-orange-100 text-orange-800',
};

const EMPTY_FORM: CreateAdminProcurementDto = {
  purchaseDate: new Date().toISOString().slice(0, 10),
  category: 'office_supplies',
  itemName: '',
  amountDecimal: '0',
};

export default function AdminProcurementListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_PROCUREMENT_PERMISSIONS.READ);
  const canWrite = hasPermission(
    permissions,
    ADMIN_PROCUREMENT_PERMISSIONS.WRITE,
  );
  const canApprove = canApproveAdminProcurement(permissions);
  const canManage = canManageAdminProcurement(permissions);
  const canSeeFinance = canSeeAdminProcurementFinance(permissions);

  const { items, total, loading, error, fetchProcurements } =
    useAdminProcurements();
  const {
    loading: mutating,
    error: mutationError,
    createProcurement,
    confirmProcurement,
    voidProcurement,
    archiveProcurement,
    requestPayment,
  } = useAdminProcurementMutations();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    AdminProcurementDto['status'] | ''
  >('');
  const [categoryFilter, setCategoryFilter] = useState<
    AdminProcurementDto['category'] | ''
  >('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<
    AdminProcurementDto['paymentStatus'] | ''
  >('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateAdminProcurementDto>(EMPTY_FORM);

  // Issue #374: payment-application generation form state. The user must
  // supply payeeAccountId + paymentMethod; overrides force extra approval.
  const [paymentTarget, setPaymentTarget] = useState<AdminProcurementDto | null>(
    null,
  );
  const [paymentForm, setPaymentForm] = useState<{
    payeeAccountId: string;
    paymentMethod: string;
    overrideBankAccountPlaintext: string;
    overrideTaxIdPlaintext: string;
    reason: string;
  }>({
    payeeAccountId: '',
    paymentMethod: 'bank_transfer',
    overrideBankAccountPlaintext: '',
    overrideTaxIdPlaintext: '',
    reason: '',
  });

  const reload = () => {
    void fetchProcurements({
      search: search || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      paymentStatus: paymentStatusFilter || undefined,
      includeArchived: includeArchived || undefined,
      page,
      limit,
    });
  };

  useEffect(() => {
    if (!canRead) return;
    void fetchProcurements({
      search: search || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      paymentStatus: paymentStatusFilter || undefined,
      includeArchived: includeArchived || undefined,
      page,
      limit,
    });
  }, [
    canRead,
    search,
    statusFilter,
    categoryFilter,
    paymentStatusFilter,
    includeArchived,
    page,
    fetchProcurements,
  ]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total],
  );

  if (!canRead) return <AccessDenied resource="行政采购台账" />;

  const handleCreate = async () => {
    if (!form.itemName.trim() || !form.amountDecimal.trim()) return;
    const created = await createProcurement(form);
    if (created) {
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      reload();
    }
  };

  const handleConfirm = async (row: AdminProcurementDto) => {
    if (!window.confirm(`确认采购单 ${row.purchaseNo}？`)) return;
    const next = await confirmProcurement(row.id, {
      expectedVersion: row.version,
    });
    if (next) reload();
  };

  const handleVoid = async (row: AdminProcurementDto) => {
    const reason = window.prompt('请输入作废原因');
    if (!reason || !reason.trim()) return;
    const next = await voidProcurement(row.id, {
      expectedVersion: row.version,
      reason: reason.trim(),
    });
    if (next) reload();
  };

  const handleArchive = async (row: AdminProcurementDto) => {
    if (!window.confirm(`归档采购单 ${row.purchaseNo}？归档后不可再编辑。`))
      return;
    const next = await archiveProcurement(row.id, {
      expectedVersion: row.version,
    });
    if (next) reload();
  };

  const handlePaymentOpen = (row: AdminProcurementDto) => {
    setPaymentTarget(row);
    setPaymentForm({
      payeeAccountId: '',
      paymentMethod: 'bank_transfer',
      overrideBankAccountPlaintext: '',
      overrideTaxIdPlaintext: '',
      reason: '',
    });
  };

  const handlePaymentSubmit = async () => {
    if (!paymentTarget) return;
    if (!paymentForm.payeeAccountId.trim()) return;
    if (!paymentForm.paymentMethod.trim()) return;
    const overrideBank = paymentForm.overrideBankAccountPlaintext.trim();
    const overrideTax = paymentForm.overrideTaxIdPlaintext.trim();
    const reason = paymentForm.reason.trim();
    const body: RequestPaymentApplicationDto = {
      expectedVersion: paymentTarget.version,
      payeeAccountId: paymentForm.payeeAccountId.trim(),
      paymentMethod: paymentForm.paymentMethod.trim(),
      ...(overrideBank && { overrideBankAccountPlaintext: overrideBank }),
      ...(overrideTax && { overrideTaxIdPlaintext: overrideTax }),
      ...(reason && { reason }),
    };
    const next = await requestPayment(paymentTarget.id, body);
    if (next) {
      setPaymentTarget(null);
      reload();
    }
  };

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">行政采购台账</h1>
          <p className="text-sm text-slate-500">
            登记行政办公用品、设备、维护类采购。确认后由 admin_procurement:finance
            权限手动生成付款申请，不会自动创建付款单；实验室 / 医疗 / 库存采购
            走独立模块。
          </p>
        </div>
        {canWrite ? (
          <button
            className="rounded-md border bg-blue-50 px-4 py-2 text-sm text-blue-700 hover:bg-blue-100"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-procurement-create-entry"
          >
            登记采购 →
          </button>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          搜索
          <input
            type="text"
            value={search}
            placeholder="采购单号或物品名称"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            data-testid="admin-procurement-search-input"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as AdminProcurementDto['status']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-procurement-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_PROCUREMENT_STATUS_LABELS) as [
                AdminProcurementDto['status'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          类别
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(
                (e.target.value as AdminProcurementDto['category']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-procurement-category-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_PROCUREMENT_CATEGORY_LABELS) as [
                AdminProcurementDto['category'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          付款
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={paymentStatusFilter}
            onChange={(e) => {
              setPaymentStatusFilter(
                (e.target.value as AdminProcurementDto['paymentStatus']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-procurement-payment-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_PROCUREMENT_PAYMENT_STATUS_LABELS) as [
                AdminProcurementDto['paymentStatus'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              setPage(1);
            }}
            data-testid="admin-procurement-include-archived"
          />
          含已归档
        </label>
        <Link
          href="/admin/procurement/movements"
          className="ml-auto text-sm text-blue-600 hover:underline"
          data-testid="admin-procurement-movements-link"
        >
          物品出入库 →
        </Link>
      </div>

      {error ? (
        <Card
          className="border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="admin-procurement-error"
        >
          {error.message}
        </Card>
      ) : null}
      {mutationError ? (
        <Card
          className="border-red-200 bg-red-50 p-4 text-sm text-red-800"
          data-testid="admin-procurement-mutation-error"
        >
          {mutationError.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">采购单号</th>
              <th className="px-3 py-2">物品</th>
              <th className="px-3 py-2">类别</th>
              <th className="px-3 py-2">金额</th>
              <th className="px-3 py-2">采购日期</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">付款状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-procurement-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-procurement-empty"
                >
                  暂无采购记录
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-slate-100"
                  data-testid={`admin-procurement-row-${it.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{it.purchaseNo}</td>
                  <td className="px-3 py-2">{it.itemName}</td>
                  <td className="px-3 py-2">
                    {ADMIN_PROCUREMENT_CATEGORY_LABELS[it.category]}
                  </td>
                  <td className="px-3 py-2">
                    {fmtMoney(it.amountDecimal, canSeeFinance)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {fmtDate(it.purchaseDate)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE_CLASS[it.status]}`}
                    >
                      {ADMIN_PROCUREMENT_STATUS_LABELS[it.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${PAYMENT_BADGE_CLASS[it.paymentStatus]}`}
                    >
                      {ADMIN_PROCUREMENT_PAYMENT_STATUS_LABELS[it.paymentStatus]}
                    </span>
                  </td>
                  <td className="px-3 py-2 space-x-2 text-xs">
                    {canApprove && it.status === 'draft' ? (
                      <button
                        className="text-blue-600 hover:underline"
                        disabled={mutating}
                        onClick={() => handleConfirm(it)}
                        data-testid={`admin-procurement-confirm-${it.id}`}
                      >
                        确认
                      </button>
                    ) : null}
                    {canApprove &&
                    (it.status === 'draft' ||
                      it.status === 'confirmed' ||
                      it.status === 'payment_requested') ? (
                      <button
                        className="text-red-600 hover:underline"
                        disabled={mutating}
                        onClick={() => handleVoid(it)}
                        data-testid={`admin-procurement-void-${it.id}`}
                      >
                        作废
                      </button>
                    ) : null}
                    {canManage &&
                    (it.status === 'confirmed' ||
                      it.status === 'payment_requested' ||
                      it.status === 'paid') ? (
                      <button
                        className="text-slate-600 hover:underline"
                        disabled={mutating}
                        onClick={() => handleArchive(it)}
                        data-testid={`admin-procurement-archive-${it.id}`}
                      >
                        归档
                      </button>
                    ) : null}
                    {canSeeFinance && it.status === 'confirmed' ? (
                      <button
                        className="text-amber-700 hover:underline"
                        disabled={mutating}
                        onClick={() => handlePaymentOpen(it)}
                        data-testid={`admin-procurement-payment-${it.id}`}
                      >
                        生成付款申请
                      </button>
                    ) : null}
                    {canSeeFinance &&
                    it.paymentApplicationNo &&
                    (it.status === 'payment_requested' ||
                      it.status === 'paid') ? (
                      <span
                        className="ml-1 text-xs text-slate-500"
                        data-testid={`admin-procurement-payment-link-${it.id}`}
                        title={`付款申请编号 ${it.paymentApplicationNo}`}
                      >
                        付款单：{it.paymentApplicationNo}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </div>

      {paymentTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="admin-procurement-payment-dialog"
        >
          <Card className="w-full max-w-lg space-y-3 p-6">
            <h2 className="text-base font-semibold">
              生成付款申请 / {paymentTarget.purchaseNo}
            </h2>
            <p className="text-xs text-slate-500">
              将进入「行政负责人审核 → 财务复核 → 出纳执行」流程。同一采购单
              已存在 effective 付款申请时不会重复创建。临时覆盖银行账号 / 税号
              将触发额外审批节点，不会写入主数据。
            </p>
            <label className="block text-sm">
              <span className="block text-slate-600">收款账户 ID（必填）</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={paymentForm.payeeAccountId}
                onChange={(e) =>
                  setPaymentForm((f) => ({
                    ...f,
                    payeeAccountId: e.target.value,
                  }))
                }
                data-testid="admin-procurement-payment-payee"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">付款方式</span>
              <select
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={paymentForm.paymentMethod}
                onChange={(e) =>
                  setPaymentForm((f) => ({
                    ...f,
                    paymentMethod: e.target.value,
                  }))
                }
                data-testid="admin-procurement-payment-method"
              >
                <option value="bank_transfer">银行转账</option>
                <option value="check">支票</option>
                <option value="cash">现金</option>
                <option value="wechat">微信</option>
                <option value="alipay">支付宝</option>
                <option value="other">其他</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">
                临时银行账号覆盖（可选 / 触发额外审批）
              </span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={paymentForm.overrideBankAccountPlaintext}
                onChange={(e) =>
                  setPaymentForm((f) => ({
                    ...f,
                    overrideBankAccountPlaintext: e.target.value,
                  }))
                }
                data-testid="admin-procurement-payment-override-bank"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">
                临时税号覆盖（可选 / 触发额外审批）
              </span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={paymentForm.overrideTaxIdPlaintext}
                onChange={(e) =>
                  setPaymentForm((f) => ({
                    ...f,
                    overrideTaxIdPlaintext: e.target.value,
                  }))
                }
                data-testid="admin-procurement-payment-override-tax"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">备注（可选）</span>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={paymentForm.reason}
                onChange={(e) =>
                  setPaymentForm((f) => ({ ...f, reason: e.target.value }))
                }
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setPaymentTarget(null)}
              >
                取消
              </button>
              <button
                className="rounded bg-amber-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                disabled={mutating || !paymentForm.payeeAccountId.trim()}
                onClick={handlePaymentSubmit}
                data-testid="admin-procurement-payment-submit"
              >
                提交付款申请
              </button>
            </div>
          </Card>
        </div>
      ) : null}

      {createOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="admin-procurement-create-dialog"
        >
          <Card className="w-full max-w-lg space-y-3 p-6">
            <h2 className="text-base font-semibold">登记行政采购</h2>
            <label className="block text-sm">
              <span className="block text-slate-600">采购日期</span>
              <input
                type="date"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.purchaseDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, purchaseDate: e.target.value }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">类别</span>
              <select
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as AdminProcurementDto['category'],
                  }))
                }
              >
                {(
                  Object.entries(ADMIN_PROCUREMENT_CATEGORY_LABELS) as [
                    AdminProcurementDto['category'],
                    string,
                  ][]
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">物品名称</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.itemName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, itemName: e.target.value }))
                }
                data-testid="admin-procurement-create-name"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">金额 (decimal-safe)</span>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.amountDecimal}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amountDecimal: e.target.value }))
                }
                data-testid="admin-procurement-create-amount"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">供应商摘要 (可选)</span>
              <input
                type="text"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.vendorNameSummary ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vendorNameSummary: e.target.value }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">用途 (可选)</span>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                value={form.purpose ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, purpose: e.target.value }))
                }
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setCreateOpen(false)}
              >
                取消
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                disabled={mutating}
                onClick={handleCreate}
                data-testid="admin-procurement-create-submit"
              >
                提交
              </button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
